import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaFacebook, FaInstagram, FaTiktok } from "react-icons/fa";
import { useLanguage } from "./LanguageContext";

/* ================== i18n ================== */
const dict = {
  ka: {
    brandSub: "სუნამოები & აქსესუარები",
    about:
      "Shopaholics — პრემიუმ არომატები, აქსესუარები და საჩუქრები ერთ სივრცეში. ოფიციალური ბრენდები, სწრაფი მიწოდება და მხარდაჭერა ქართულსა და ინგლისურ ენებზე.",
    quickLinks: "სწრაფი ლინკები",
    collections: "კოლექციები",
    accessories: "აქსესუარები",
    aboutUs: "ჩვენ შესახებ",
    contact: "კონტაქტი",
    support: "მომხმარებლის მხარდაჭერა",
    shipping: "მიწოდება",
    orders: "ჩემი შეკვეთები",
    privacy: "პიროვნულ მონაცემთა დაცვა",
    terms: "წესები და პირობები",
    contactTitle: "კონტაქტი",
    phone: "ტელეფონი",
    email: "ელფოსტა",
    address: "მისამართი",
    tbilisiGeo: "თბილისი, საქართველო",
    hours: "სამუშაო საათები",
    hoursBadge: "ორშ–კვ: 10:00–19:00",
    newsletterTitle: "გამოიწერე ნიუსლეთერი",
    newsletterDesc: "იყავი პირვლებიდან, ვინც გაიგებს ფასდაკლებებსა და ახალ კოლექციებს.",
    placeholderEmail: "შეიყვანე ელფოსტა",
    subscribe: "გამოწერა",
    consent1: "გამოწერით ეთანხმებით ჩვენს ",
    consent2: "კონფიდენციალურობის პოლიტიკას",
    cookies: "Cookie Policy",
    allRights: (y) => `© ${y} შ.პ.ს. შოპაჰოლიქს (405272529). ყველა უფლება დაცულია.`,
    ariaSocial: "სოციალური ქსელები",
    ariaFb: "Facebook",
    ariaIg: "Instagram",
    ariaTt: "TikTok",
    perfume : "პარფიუმერია",
    hairandbodycare: "თმის და ტანის მოვლა",
    skincare: "კანის მოვლა",
  },
  en: {
    brandSub: "Perfume & Accessories",
    about:
      "Shopaholics — premium fragrances, accessories, and gifts in one place. Official brands, fast delivery, and support in Georgian & English.",
    quickLinks: "Quick Links",
    collections: "Collections",
    accessories: "Accessories",
    aboutUs: "About Us",
    contact: "Contact",
    support: "Customer Support",
    shipping: "Shipping",
    orders: "My Orders",
    privacy: "Privacy Policy",
    terms: "Terms & Conditions",
    contactTitle: "Contact",
    phone: "Phone",
    email: "Email",
    address: "Address",
    tbilisiGeo: "Tbilisi, Georgia",
    hours: "Working Hours",
    hoursBadge: "Mon–Sun: 10:00–19:00",
    newsletterTitle: "Subscribe to Newsletter",
    newsletterDesc:
      "Be the first to hear about discounts and new collections.",
    placeholderEmail: "Enter your email",
    subscribe: "Subscribe",
    consent1: "By subscribing you agree to our ",
    consent2: "Privacy Policy",
    cookies: "Cookie Policy",
    allRights: (y) => `© ${y} L.T.D. Shopaholics (405272529). All rights reserved.`,
    ariaSocial: "Social links",
    ariaFb: "Facebook",
    ariaIg: "Instagram",
    ariaTt: "TikTok",
    perfume : "Perfume",
    hairandbodycare: "Hair and body care",
    skincare: "Skin care"
  },
};

const t = (lang, key, ...args) =>
  typeof dict[lang][key] === "function"
    ? dict[lang][key](...args)
    : dict[lang][key];

/* ============= Brand palette (შეგიძლია შეცვალო) ============= */
const colors = {
  bg: "#ffffff",
  border: "rgba(0,0,0,0.08)",
  text: "#0f0f0f",
  dim: "#6b6b6b",
  accent: "#111827",
  accentSoft: "rgba(17,24,39,0.06)",
};

/* ============= Layout ============= */
const FooterContainer = styled.footer`
  width: 100%;
  color: ${colors.text};
  background:
    radial-gradient(1100px 400px at 50% -240px, rgba(17,24,39,0.06), transparent),
    ${colors.bg};
  border-top: 1px solid ${colors.border};
`;

const Wrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 42px 20px 16px;
`;

const TopArea = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1.2fr;
  gap: 32px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const Column = styled.div``;

const Title = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  margin: 0 0 14px;
`;

const Text = styled.p`
  margin: 0 0 10px;
  line-height: 1.65;
  color: ${colors.dim};
`;

const Muted = styled.span`
  color: ${colors.dim};
`;

const BrandCard = styled.div`
  background: #fff;
  border: 1px solid ${colors.border};
  border-radius: 14px;
  padding: 18px;
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 14px;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
`;

const Logo = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.08));
`;

const BrandName = styled.div`
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: 0.3px;
`;

const BrandSub = styled.div`
  font-size: 0.9rem;
  color: ${colors.dim};
`;

const List = styled.ul`
  list-style: none;
  padding: 0; margin: 0;
  display: grid;
  gap: 10px;
  font-size: 0.9rem;
`;

const ItemLink = styled(Link)`
  text-decoration: none;
  color: ${colors.text};
  opacity: 0.9;
  transition: 0.2s ease;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const ExtLink = styled.a`
  text-decoration: none;
  color: ${colors.text};
  opacity: 0.9;
  transition: 0.2s ease;
  &:hover { opacity: 1; text-decoration: underline; }
`;

const InfoRow = styled.div`
  display: grid;
  gap: 8px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 6px 10px;
  font-size: 0.8rem;
  border-radius: 999px;
  background: ${colors.accentSoft};
  border: 1px solid ${colors.border};
  color: ${colors.text};
`;

const Socials = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 10px;
`;

const SocialBtn = styled.a`
  display: inline-flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; border-radius: 10px;
  border: 1px solid ${colors.border};
  background: #fff;
  transition: transform .15s ease, box-shadow .15s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);

  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }

  svg { font-size: 1.15rem; color: ${colors.text}; }
`;

const Newsletter = styled.form`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  @media (max-width: 420px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  height: 44px;
  border: 1px solid ${colors.border};
  background: #fff;
  border-radius: 12px;
  padding: 0 14px;
  font-size: 0.95rem;
  outline: none;
`;

const Button = styled.button`
  height: 44px;
  padding: 0 16px;
  border: 1px solid ${colors.border};
  background: #000;
  color: #fff;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, opacity .2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.12); }
  &:active { transform: translateY(0); opacity: .9; }
`;

const PayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const PayBadge = styled.img`
  height: 26px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  border: 1px solid ${colors.border};
  padding: 6px 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.border};
  margin: 26px 0 10px;
`;

const BottomBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-bottom: 8px;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 16px;
  a {
    color: ${colors.dim};
    text-decoration: none;
  }
  a:hover { text-decoration: underline; }
`;

const Copy = styled.div`
  color: ${colors.dim};
  font-size: 0.9rem;
`;

/* ============= Component ============= */
const Footer = () => {
  const year = new Date().getFullYear();
  const { language } = useLanguage(); // 'ka' | 'en'

  return (
    <FooterContainer>
      <Wrapper>
        <TopArea>
          {/* --- Column A: Brand / About --- */}
          <Column>
            <BrandCard>
              <Logo src="/images/logo.png" alt="Shopaholics logo" />
              <div>
                <BrandName>Shopaholics</BrandName>
                <BrandSub>{t(language, "brandSub")}</BrandSub>
              </div>
            </BrandCard>
            <Text style={{ marginTop: 12, fontSize: 14 }}>
              {t(language, "about")}
            </Text>
            <Socials aria-label={t(language, "ariaSocial")}>
              <SocialBtn
                href="https://facebook.com/shopaholicstbilisigeorgia"
                target="_blank"
                rel="noreferrer"
                aria-label={t(language, "ariaFb")}
              >
                <FaFacebook />
              </SocialBtn>
              <SocialBtn
                href="https://instagram.com/shopaholicstbilisi"
                target="_blank"
                rel="noreferrer"
                aria-label={t(language, "ariaIg")}
              >
                <FaInstagram />
              </SocialBtn>
              <SocialBtn
                href="https://tiktok.com/shopaholicstbilisi"
                target="_blank"
                rel="noreferrer"
                aria-label={t(language, "ariaTt")}
              >
                <FaTiktok />
              </SocialBtn>
            </Socials>
          </Column>

          {/* --- Column B: Quick Links --- */}
          <Column>
            <Title>{t(language, "quickLinks")}</Title>
            <List>
              <li><ItemLink to="/collections">{t(language, "collections")}</ItemLink></li>
              <li><ItemLink to="/products?category=accessories">{t(language, "accessories")}</ItemLink></li>
              <li><ItemLink to="/products?category=perfume">{t(language, "perfume")}</ItemLink></li>
              <li><ItemLink to="/products?category=hair-and-body-care">{t(language, "hairandbodycare")}</ItemLink></li>
              <li><ItemLink to="/products?category=skin-care">{t(language, "skincare")}</ItemLink></li>
              <li><ItemLink to="/about-us">{t(language, "aboutUs")}</ItemLink></li>
              
            </List>
          </Column>

          {/* --- Column C: Support --- */}
          <Column>
            <Title>{t(language, "support")}</Title>
            <List>
              <li><ItemLink to="/my-purchases">{t(language, "orders")}</ItemLink></li>
              <li><ItemLink to="/privacy">{t(language, "privacy")}</ItemLink></li>
              <li><ItemLink to="/terms">{t(language, "terms")}</ItemLink></li>
              <li><ItemLink to="/cookies">{t(language, "cookies")}</ItemLink></li>    
            </List>
            <PayRow>
              <svg xmlns="http://www.w3.org/2000/svg" width="88" height="26" viewBox="0 0 88 26" style={{borderRadius:8}}>
                <rect x="0.5" y="0.5" rx="6" ry="6" width="87" height="25" fill="#fff" stroke="rgba(0,0,0,0.12)"/>
                <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
                      fontFamily="ui-sans-serif, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
                      fontSize="12" fill="#111827" fontWeight="700">FLITT</text>
              </svg>
            </PayRow>
          </Column>

          {/* --- Column D: Contact + Newsletter --- */}
          <Column>
            <Title>{t(language, "contactTitle")}</Title>
            <InfoRow>
              <Text style={{ fontSize: 14 }}>
                <strong>{t(language, "phone")}:</strong>{" "}
                <ExtLink href="tel:+995558641621">+995 558 641 621</ExtLink>
              </Text>
              <Text style={{ fontSize: 14 }}>
                <strong>{t(language, "email")}:</strong>{" "}
                <ExtLink href="mailto:support@shopaholics.ge">support@shopaholics.ge</ExtLink>
              </Text>
              <Text style={{ fontSize: 14 }}>
                <strong>{t(language, "address")}:</strong> {t(language, "tbilisiGeo")}
              </Text>
              <Text style={{ fontSize: 14 }}>
                <strong>{t(language, "hours")}:</strong>{" "}
                <Badge>{t(language, "hoursBadge")}</Badge>
              </Text>
            </InfoRow>

            <Title style={{ marginTop: 18 }}>{t(language, "newsletterTitle")}</Title>
            <Text style={{ fontSize: 14 }}>{t(language, "newsletterDesc")}</Text>
            <Newsletter
              onSubmit={(e) => {
                e.preventDefault();
                alert(language === "ka" ? "გმადლობთ გამოწერისთვის!" : "Thanks for subscribing!");
              }}
            >
              <Input
                type="email"
                placeholder={t(language, "placeholderEmail")}
                aria-label={t(language, "email")}
                required
              />
              <Button type="submit">{t(language, "subscribe")}</Button>
            </Newsletter>
            <Text style={{ fontSize: "0.85rem", marginTop: 8 }}>
              <Muted>{t(language, "consent1")}</Muted>
              <ItemLink to="/privacy">{t(language, "consent2")}</ItemLink>.
            </Text>
          </Column>
        </TopArea>

        <Divider />

        <BottomBar>
          <Copy>{t(language, "allRights", year)}</Copy>
        </BottomBar>
      </Wrapper>
    </FooterContainer>
  );
};

export default Footer;
